import type { JSX } from "react";
import { useEffect, useState } from "react";
import type {
  ResolutionBlock,
  ResolutionStep,
  ResolutionStepState
} from "@shared/types";
import type { BlockRendererProps } from "./registry";

/**
 * The explicit, operable resolution panel (OpenSpec answer-rendering-lanes.md,
 * Feature 2). Renders the resolution object as native, themed UI: an ordered
 * (optionally branching) checklist the user marks done / failed. Mutations are
 * persisted through the backend (`onMutateStep`); the panel is controlled by the
 * `block` prop, so once the parent applies the returned block the panel
 * re-renders with the authoritative state. Honors the Cycle Design System
 * (tokens, sentence case, color reserved for the single primary action).
 */

/** Human-readable state label (sentence case, no glyphs). */
function stateLabel(state: ResolutionStepState): string {
  switch (state) {
    case "done":
      return "已完成";
    case "active":
      return "当前步";
    case "failed":
      return "未解决";
    default:
      return "未开始";
  }
}

/** A small check mark for completed steps (SVG icon, not a decorative glyph). */
function CheckMark(): JSX.Element {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
      <path
        d="M2.5 6.5l2.2 2.2L9.5 3.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ResolutionPanelView({
  block,
  onSendPrompt,
  onMutateStep
}: BlockRendererProps): JSX.Element {
  const panel = block as ResolutionBlock;
  // The step currently awaiting the server's response, so its buttons disable
  // until the parent re-renders the panel with the persisted block.
  const [busyStepId, setBusyStepId] = useState<string | null>(null);
  useEffect(() => {
    // The block changed (the parent applied the mutation) — clear the busy lock.
    setBusyStepId(null);
  }, [block]);

  const doneCount = panel.steps.filter((s) => s.state === "done").length;
  const mutate = (step: ResolutionStep, state: ResolutionStepState): void => {
    setBusyStepId(step.id);
    onMutateStep(panel.id, step.id, state);
  };

  return (
    <div className="resolution-panel" data-status={panel.status}>
      <div className="resolution-head">
        <span className="resolution-title">{panel.title}</span>
        <span className="resolution-progress">
          {doneCount} / {panel.steps.length} 完成
        </span>
      </div>
      {panel.prerequisite !== undefined && panel.prerequisite.length > 0 && (
        <div className="resolution-prereq">
          <span className="resolution-prereq-label">前提</span>
          {panel.prerequisite}
        </div>
      )}
      <ol className="resolution-steps">
        {panel.steps.map((s, i) => {
          const busy = busyStepId !== null;
          return (
            <li key={s.id} className="resolution-step" data-state={s.state}>
              <span className="resolution-step-marker" aria-hidden="true">
                {s.state === "done" ? <CheckMark /> : i + 1}
              </span>
              <div className="resolution-step-main">
                <div className="resolution-step-label">
                  <span>{s.label}</span>
                  <span className="resolution-step-state">{stateLabel(s.state)}</span>
                </div>
                {s.detail !== undefined && s.detail.length > 0 && s.state === "active" && (
                  <div className="resolution-step-detail">{s.detail}</div>
                )}
                {s.state === "active" && (
                  <div className="resolution-step-actions">
                    <button
                      type="button"
                      className="primary"
                      disabled={busy}
                      onClick={() => mutate(s, "done")}
                    >
                      标记完成
                    </button>
                    <button type="button" disabled={busy} onClick={() => mutate(s, "failed")}>
                      这步没成功
                    </button>
                  </div>
                )}
                {s.state === "failed" && (
                  <button
                    type="button"
                    className="resolution-step-help"
                    onClick={() =>
                      onSendPrompt(
                        `我在「${s.label}」这步卡住了，能帮我排查吗？不行的话请帮我转人工。`
                      )
                    }
                  >
                    就这步求助
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      {panel.status === "resolved" && (
        <div className="resolution-resolved-note">全部步骤已完成</div>
      )}
    </div>
  );
}
